import React from 'react';

import {useAdminAccessRight} from 'src/hooks/access-rights';
import {PageNotFound} from 'src/modules/error-page';
import {ErrorPage} from 'src/modules/error-page/error-page.components';

import type {AccessRight} from 'src/hooks/access-rights/constants';
import type {Error} from 'src/openapi/admin/models/Error';

type Props = {
    children: React.ReactElement,
    serverError: Error | null,
    accessRight?: AccessRight,
    sectionName?: string,
    smallView?: boolean,
    showDefaultError?: boolean,
    errorTitle?: string,
    errorSubtitle?: string,
};

const LOCKED_FEATURE_STATUS = 423;
const ACCESS_DENIED_STATUS = 403;

export const ServerResponseHandler = (props: Props) => {
    const {children, serverError, accessRight, sectionName, smallView, showDefaultError = true} = props;
    const {hasAccess} = useAdminAccessRight();

    if (serverError?.status === LOCKED_FEATURE_STATUS) {
        return (
            <ErrorPage
                title={`The ${sectionName || 'feature'} is locked`}
                subtitle="Contact your manager."
                small={smallView}
            />
        );
    }

    if (accessRight && !hasAccess(accessRight) || serverError?.status === ACCESS_DENIED_STATUS) {
        return showDefaultError ? (
            <ErrorPage
                title={sectionName ? `Access to ${sectionName} denied` : 'Access denied'}
                subtitle="Contact your manager."
                small={smallView}
            />
        ) : <div/>;
    }

    if (serverError?.status === 404) {
        return (
            <PageNotFound/>
        );
    }

    if (serverError) {
        return (
            <ErrorPage
                title={props.errorTitle ?? `${sectionName || 'The'} section is temporarily unavailable`}
                subtitle={props.errorSubtitle ?? 'Please try again later.'}
                small={smallView}
            />
        );
    }

    return children;
};
